import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { errorMessage, unwrap } from 'api/call'
import {
  deleteAdminExtraMenuItemsById,
  getAdminExtraMenuItems,
  postAdminExtraMenuItems,
  putAdminExtraMenuItemsById,
  type ExtraMenuItemAdmin,
  type ExtraMenuItemInput,
} from 'api/generated'
import { validationMessage } from 'helpers/validation-message'
import { toast } from 'sonner'

interface ExtraMenuItemsState {
  loading: boolean
  saving: boolean
  items: ExtraMenuItemAdmin[]
}

const initialState: ExtraMenuItemsState = {
  loading: false,
  saving: false,
  items: [],
}

// Stesso ordine dell'app: posizione, poi id.
const sorted = (items: ExtraMenuItemAdmin[]) =>
  [...items].sort((a, b) => a.position - b.position || a.id - b.id)

export const fetchExtraMenuItems = createAsyncThunk<
  ExtraMenuItemAdmin[],
  void,
  { rejectValue: string }
>('extraMenuItems/fetchExtraMenuItems', async (_, { rejectWithValue }) => {
  try {
    const { data } = await unwrap(getAdminExtraMenuItems())
    return data
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Errore nel caricamento.'))
  }
})

export const createExtraMenuItem = createAsyncThunk<
  ExtraMenuItemAdmin,
  ExtraMenuItemInput & { label: string; url: string },
  { rejectValue: string }
>('extraMenuItems/createExtraMenuItem', async (input, { rejectWithValue }) => {
  try {
    const { extra_menu_item } = await unwrap(
      postAdminExtraMenuItems({ body: { extra_menu_item: input } })
    )
    toast.success('Voce creata')
    return extra_menu_item
  } catch (error) {
    const message = validationMessage(error, 'Salvataggio fallito.')
    toast.error(message)
    return rejectWithValue(message)
  }
})

export const updateExtraMenuItem = createAsyncThunk<
  ExtraMenuItemAdmin,
  { id: number; input: ExtraMenuItemInput },
  { rejectValue: string }
>(
  'extraMenuItems/updateExtraMenuItem',
  async ({ id, input }, { rejectWithValue }) => {
    try {
      const { extra_menu_item } = await unwrap(
        putAdminExtraMenuItemsById({
          path: { id },
          body: { extra_menu_item: input },
        })
      )
      toast.success('Voce salvata')
      return extra_menu_item
    } catch (error) {
      const message = validationMessage(error, 'Salvataggio fallito.')
      toast.error(message)
      return rejectWithValue(message)
    }
  }
)

export const deleteExtraMenuItem = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('extraMenuItems/deleteExtraMenuItem', async (id, { rejectWithValue }) => {
  try {
    await unwrap(deleteAdminExtraMenuItemsById({ path: { id } }))
    toast.success('Voce eliminata')
    return id
  } catch (error) {
    toast.error('Impossibile eliminare la voce.')
    return rejectWithValue(errorMessage(error, 'Eliminazione fallita.'))
  }
})

const extraMenuItemsSlice = createSlice({
  name: 'extraMenuItems',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchExtraMenuItems.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchExtraMenuItems.fulfilled, (state, action) => {
        state.loading = false
        state.items = sorted(action.payload)
      })
      .addCase(fetchExtraMenuItems.rejected, (state) => {
        state.loading = false
        toast.error('Errore nel caricamento delle voci del menu')
      })
      .addCase(createExtraMenuItem.pending, (state) => {
        state.saving = true
      })
      .addCase(createExtraMenuItem.fulfilled, (state, action) => {
        state.saving = false
        state.items = sorted([...state.items, action.payload])
      })
      .addCase(createExtraMenuItem.rejected, (state) => {
        state.saving = false
      })
      .addCase(updateExtraMenuItem.pending, (state) => {
        state.saving = true
      })
      .addCase(updateExtraMenuItem.fulfilled, (state, action) => {
        state.saving = false
        state.items = sorted(
          state.items.map((item) =>
            item.id === action.payload.id ? action.payload : item
          )
        )
      })
      .addCase(updateExtraMenuItem.rejected, (state) => {
        state.saving = false
      })
      .addCase(deleteExtraMenuItem.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload)
      })
  },
})

export const extraMenuItemsReducer = extraMenuItemsSlice.reducer
